/**
 * EventDetailsSections - Collection of toggleable detail sections for event forms
 * Extracted from EventsTab.jsx to reduce file size
 */
import { useState } from "react";
import { Plus, Trash2, Users, Trophy, Video, Search, X, ExternalLink, BarChart3 } from "lucide-react";
import { Button } from "../../ui/button";
import { ImageUpload } from "../ImageUpload";

// Flagship Sponsor Section
export const FlagshipSponsorSection = ({ formData, setFormData }) => (
  <div className="p-4 bg-amber-500/5 border border-amber-500/20 mb-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Trophy className="w-5 h-5 text-amber-500" />
        <div>
          <h5 className="font-secondary text-xs font-bold uppercase text-amber-500">Flagship Sponsor</h5>
          <p className="text-[10px] text-zinc-500">Display a prominent sponsor on the event page header</p>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={formData.flagship_sponsor_enabled}
          onChange={(e) => setFormData({...formData, flagship_sponsor_enabled: e.target.checked})}
          className="w-4 h-4"
        />
        <span className="text-xs text-zinc-400">Enable</span>
      </label>
    </div>
    
    {formData.flagship_sponsor_enabled && (
      <div className="space-y-3 pt-3 border-t border-amber-500/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Name</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_name}
              onChange={(e) => setFormData({...formData, flagship_sponsor_name: e.target.value})}
              placeholder="e.g., Nike"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Logo URL</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_logo_url}
              onChange={(e) => setFormData({...formData, flagship_sponsor_logo_url: e.target.value})}
              placeholder="https://..."
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Website URL</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_url}
              onChange={(e) => setFormData({...formData, flagship_sponsor_url: e.target.value})}
              placeholder="https://sponsor-website.com"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
        </div>
        {formData.flagship_sponsor_logo_url && (
          <div className="flex items-center gap-4 p-3 bg-zinc-900/50 border border-amber-500/20 rounded">
            <img src={formData.flagship_sponsor_logo_url} alt="Flagship Sponsor" className="h-12 max-w-[150px] object-contain" />
            <div className="text-xs text-zinc-500">
              <span className="text-amber-500 font-bold">Presented by</span> {formData.flagship_sponsor_name || "Sponsor"}
              {formData.flagship_sponsor_url && (
                <a href={formData.flagship_sponsor_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300">
                  <ExternalLink className="w-3 h-3 inline" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

// Tournament Banner Section - Replaces ad banner on game recap pages for event games
export const TournamentBannerSection = ({ formData, setFormData }) => {
  const handleBannerChange = (url) => {
    setFormData({...formData, tournament_banner_url: url});
  };

  return (
    <div className="p-4 bg-purple-500/5 border border-purple-500/20 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-purple-500/20 rounded flex items-center justify-center">
          <span className="text-purple-500 text-lg">📺</span>
        </div>
        <div>
          <h5 className="font-secondary text-xs font-bold uppercase text-purple-500">Tournament Banner</h5>
          <p className="text-[10px] text-zinc-500">
            This banner replaces the ad space at the top of game recap pages for games in this event
          </p>
        </div>
      </div>
      
      <div className="space-y-3">
        <ImageUpload
          value={formData.tournament_banner_url || ''}
          onChange={handleBannerChange}
          label="Banner Image"
          placeholder="Enter URL or upload file"
          previewSize="lg"
          id="tournament-banner-upload"
        />
        
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Banner Link (Optional)</label>
          <input 
            type="text"
            value={formData.tournament_banner_link || ''}
            onChange={(e) => setFormData({...formData, tournament_banner_link: e.target.value})}
            placeholder="https://... (opens in new tab when clicked)"
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

// Sponsor Add Form Section
export const SponsorAddSection = ({ formData, setFormData, newSponsor, setNewSponsor }) => {
  const handleAddSponsor = () => {
    if (!newSponsor.name.trim()) return;
    setFormData({
      ...formData, 
      sponsors: [...formData.sponsors, { ...newSponsor }]
    });
    setNewSponsor({ name: "", logo_url: "", banner_url: "" });
  };

  const handleRemoveSponsor = (index) => {
    setFormData({
      ...formData,
      sponsors: formData.sponsors.filter((_, i) => i !== index)
    });
  };

  return (
    <>
      {/* Add Sponsor Form */}
      <div className="space-y-3 mb-4 p-4 bg-zinc-800/30 border border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input 
            type="text" 
            value={newSponsor.name} 
            onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})}
            placeholder="Sponsor Name *"
            className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
          <div></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input 
            type="url" 
            value={newSponsor.logo_url} 
            onChange={(e) => setNewSponsor({...newSponsor, logo_url: e.target.value})}
            placeholder="Logo URL"
            className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
          <input 
            type="url" 
            value={newSponsor.banner_url} 
            onChange={(e) => setNewSponsor({...newSponsor, banner_url: e.target.value})}
            placeholder="Banner URL (Optional)"
            className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={handleAddSponsor}
          disabled={!newSponsor.name.trim()}
          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-400 disabled:opacity-50"
        >
          <Plus className="w-3 h-3" /> Add Sponsor
        </button>
      </div>

      {/* Existing Sponsors */}
      {formData.sponsors?.length > 0 && (
        <div className="space-y-2 p-4 bg-zinc-800/50 border border-white/5">
          <p className="text-xs text-zinc-500 mb-2">{formData.sponsors.length} sponsor(s) added</p>
          {formData.sponsors.map((sponsor, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 bg-zinc-900 border border-white/10 rounded">
              {sponsor.logo_url && (
                <img src={sponsor.logo_url} alt="" className="w-10 h-10 object-contain" />
              )}
              <div className="flex-1">
                <p className="text-white text-sm">{sponsor.name}</p>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveSponsor(idx)}
                className="text-red-500 hover:text-red-400 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Video Playlist Section
export const VideoPlaylistEditSection = ({ 
  formData, 
  setFormData,
  newVideoUrl,
  setNewVideoUrl,
  newVideoTitle,
  setNewVideoTitle,
  addVideoToPlaylist,
  removeVideoFromPlaylist
}) => (
  <div className="border-t border-white/10 pt-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Video className="w-5 h-5 text-purple-500" />
        <div>
          <h4 className="font-secondary text-sm font-bold uppercase text-white">Video Playlist</h4>
          <p className="text-[10px] text-zinc-500">Add YouTube videos to display on the event page</p>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={formData.video_playlist_enabled}
          onChange={(e) => setFormData({...formData, video_playlist_enabled: e.target.checked})}
          className="w-4 h-4"
          data-testid="video-playlist-toggle"
        />
        <span className="text-xs text-zinc-400">Enable</span>
      </label>
    </div>

    {formData.video_playlist_enabled && (
      <div className="space-y-4 p-4 bg-purple-500/5 border border-purple-500/20">
        {/* Section Title */}
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Section Title</label>
          <input 
            type="text"
            value={formData.video_playlist_title}
            onChange={(e) => setFormData({...formData, video_playlist_title: e.target.value})}
            placeholder="Event Videos"
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="video-playlist-title"
          />
        </div>

        {/* Add Video Form */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="md:col-span-2">
            <input 
              type="text"
              value={newVideoUrl}
              onChange={(e) => setNewVideoUrl(e.target.value)}
              placeholder="YouTube URL (video or playlist)"
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
              data-testid="video-url-input"
            />
          </div>
          <div className="flex gap-2">
            <input 
              type="text"
              value={newVideoTitle}
              onChange={(e) => setNewVideoTitle(e.target.value)}
              placeholder="Title (optional)"
              className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
            />
            <Button
              type="button"
              onClick={addVideoToPlaylist}
              disabled={!newVideoUrl.trim()}
              className="bg-purple-600 text-white hover:bg-purple-500 text-xs px-3 h-auto rounded-none"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Video List */}
        {formData.video_playlist.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {formData.video_playlist.map((video) => (
              <div key={video.id} className="relative group bg-zinc-900 border border-white/10">
                {video.thumbnail_url && (
                  <img 
                    src={video.thumbnail_url} 
                    alt={video.title}
                    className="w-full aspect-video object-cover"
                  />
                )}
                <div className="p-2">
                  <p className="text-xs text-white truncate">{video.title}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeVideoFromPlaylist(video.id)}
                  className="absolute top-1 right-1 p-1 bg-black/70 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
  </div>
);

// Stat Leaders Section
export const StatLeadersSection = ({ formData, setFormData }) => (
  <div className="border-t border-white/10 pt-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <BarChart3 className="w-5 h-5 text-green-500" />
        <div>
          <h4 className="font-secondary text-sm font-bold uppercase text-white">Stat Leaders</h4>
          <p className="text-[10px] text-zinc-500">Show top players in Points, Rebounds, and Assists on event page</p>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={formData.stat_leaders_enabled}
          onChange={(e) => setFormData({...formData, stat_leaders_enabled: e.target.checked})}
          className="w-4 h-4"
          data-testid="stat-leaders-toggle"
        />
        <span className="text-xs text-zinc-400">Enable</span>
      </label>
    </div>

    {formData.stat_leaders_enabled && (
      <div className="p-4 bg-green-500/5 border border-green-500/20">
        <p className="text-xs text-zinc-400">
          <strong>Note:</strong> Stat leaders are automatically pulled from games that have a 
          <span className="text-green-400"> Stats Link</span> attached in the bracket editor. 
          Make sure your bracket games have valid StatMoose or PrestoSports links.
        </p>
      </div>
    )}
  </div>
);

// SEO Settings Section
export const SEOSettingsSection = ({ 
  formData, 
  setFormData,
  newKeyword,
  setNewKeyword,
  addKeyword,
  removeKeyword
}) => (
  <div className="border-t border-white/10 pt-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Search className="w-5 h-5 text-blue-500" />
        <div>
          <h4 className="font-secondary text-sm font-bold uppercase text-white">SEO Settings</h4>
          <p className="text-[10px] text-zinc-500">Optimize for search engines</p>
        </div>
      </div>
    </div>

    <div className="p-4 bg-blue-500/5 border border-blue-500/20 space-y-4">
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase mb-1">Meta Description</label>
        <textarea 
          value={formData.seo_settings.meta_description}
          onChange={(e) => setFormData({
            ...formData, 
            seo_settings: {...formData.seo_settings, meta_description: e.target.value}
          })}
          placeholder="Brief description for search results (150-160 characters recommended)"
          rows={2}
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm resize-none"
        />
        <p className="text-[10px] text-zinc-600 mt-1">
          {formData.seo_settings.meta_description?.length || 0}/160 characters
        </p>
      </div>

      <div>
        <label className="block text-[10px] text-zinc-500 uppercase mb-1">Open Graph Image URL</label>
        <input 
          type="url"
          value={formData.seo_settings.og_image_url}
          onChange={(e) => setFormData({
            ...formData,
            seo_settings: {...formData.seo_settings, og_image_url: e.target.value}
          })}
          placeholder="Image URL for social media sharing"
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-[10px] text-zinc-500 uppercase mb-1">Keywords</label>
        <div className="flex gap-2 mb-2">
          <input 
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Add keyword..."
            className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }}}
          />
          <Button
            type="button"
            onClick={addKeyword}
            disabled={!newKeyword.trim()}
            className="bg-blue-600 text-white hover:bg-blue-500 text-xs px-3 h-auto rounded-none"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.seo_settings.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.seo_settings.keywords.map((kw, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">
                {kw}
                <button
                  type="button"
                  onClick={() => removeKeyword(idx)}
                  className="text-blue-300 hover:text-blue-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
